// Highly layered, pop-out modal button for special actions
import classNames from "classnames"
import React from "react"

type ModalButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode
  className?: string
  color?: "default" | "green" | "blue" | "red" | "yellow"
  size?: "default" | "sm" | "md" | "lg"
}

const ModalButton: React.FC<ModalButtonProps> = ({
  children,
  className,
  disabled,
  color = "default",
  size = "default",
  ...rest
}) => {
  // Color variants
  const colorClasses =
    color === "green"
      ? [
          "bg-gradient-to-br from-green-500 via-green-600 to-green-700",
          "border-green-300/30",
          "shadow-[0_8px_32px_0_rgba(34,197,94,0.18),0_2px_8px_0_rgba(255,255,255,0.10)_inset,0_0.5px_0.5px_0_rgba(255,255,255,0.15)_inset,0_0_0_8px_rgba(34,197,94,0.10)]",
        ]
      : color === "blue"
      ? [
          "bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700",
          "border-blue-300/30",
          "shadow-[0_8px_32px_0_rgba(59,130,246,0.18),0_2px_8px_0_rgba(255,255,255,0.10)_inset,0_0.5px_0.5px_0_rgba(255,255,255,0.15)_inset,0_0_0_8px_rgba(59,130,246,0.10)]",
        ]
      : color === "red"
      ? [
          "bg-gradient-to-br from-red-500 via-red-500 to-red-600",
          "border-red-300/30",
          "shadow-[0_8px_32px_0_rgba(239,68,68,0.18),0_2px_8px_0_rgba(255,255,255,0.10)_inset,0_0.5px_0.5px_0_rgba(255,255,255,0.15)_inset,0_0_0_8px_rgba(239,68,68,0.10)]",
        ]
      : color === "yellow"
      ? [
          "bg-gradient-to-br from-yellow-500 via-yellow-600 to-orange-600",
          "border-yellow-700/30",
          "shadow-[0_8px_32px_0_rgba(202,138,4,0.18),0_2px_8px_0_rgba(255,255,255,0.10)_inset,0_0.5px_0.5px_0_rgba(255,255,255,0.15)_inset,0_0_0_8px_rgba(202,138,4,0.10)]",
        ]
      : [
          "bg-gradient-to-br from-white/10 via-white/5 to-black/30 backdrop-blur-xl",
          "border-white/20",
          "shadow-[0_8px_32px_0_rgba(0,0,0,0.55),0_2px_8px_0_rgba(255,255,255,0.12)_inset,0_0.5px_0.5px_0_rgba(255,255,255,0.18)_inset,0_0_0_8px_rgba(255,255,255,0.10)]",
        ]

  // Border glow for pop effect
  const borderGlow =
    color === "green"
      ? "0 0 16px 2px rgba(34,197,94,0.18)"
      : color === "blue"
      ? "0 0 16px 2px rgba(59,130,246,0.18)"
      : color === "red"
      ? "0 0 16px 2px rgba(239,68,68,0.18)"
      : color === "yellow"
      ? "0 0 16px 2px rgba(202,138,4,0.15)"
      : "0 0 16px 2px rgba(255,255,255,0.10)"

  // Size variants
  const sizeClasses =
    size === "sm"
      ? "px-4 py-2 text-sm rounded-xl"
      : size === "md"
      ? "px-7 py-2.5 text-base rounded-xl"
      : size === "lg"
      ? "px-8 py-4 text-lg rounded-xl"
      : "px-10 py-5 text-xl rounded-2xl"

  return (
    <button
      {...rest}
      disabled={disabled}
      className={classNames(
        "relative inline-flex items-center justify-center font-poppins font-bold text-white select-none overflow-hidden border-2",
        colorClasses,
        sizeClasses,
        "transition-all duration-150 ease-in-out",
        "hover:scale-110 hover:shadow-[0_16px_48px_0_rgba(0,0,0,0.65),0_4px_16px_0_rgba(255,255,255,0.16)_inset]",
        "active:scale-98 active:shadow-[0_4px_12px_0_rgba(0,0,0,0.35),0_1.5px_4px_0_rgba(255,255,255,0.12)_inset]",
        {
          "opacity-60 cursor-not-allowed grayscale": disabled,
        },
        className
      )}
      style={{
        textShadow: "0 2px 8px rgba(0,0,0,0.25), 0 1px 0 #fff3",
        boxShadow: borderGlow,
      }}
    >
      <span className="relative z-10 flex items-center justify-center w-full h-full">
        {children}
      </span>
      {/* Extra glassy overlay */}
      <span
        className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/30 via-white/10 to-transparent opacity-70"
        style={{ borderRadius: size === "default" ? "1rem" : "0.75rem" }}
      />
      {/* Animated highlight reflection */}
      <span
        className="absolute left-4 right-4 top-2 h-3 pointer-events-none bg-white/40 blur-md opacity-40 animate-pulse"
        style={{ borderRadius: "0.5rem" }}
      />
    </button>
  )
}

export default ModalButton
